/**
 * ForgotPassword — сброс забытого пароля команды: телефон → код из SMS и
 * новый пароль → вход. Код годится только на смену пароля, войти им нельзя.
 * Ответ на запрос кода одинаков и для чужого номера — не подсказываем,
 * есть ли такой аккаунт.
 */

import {useState} from 'react';
import {Link} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';

import {requestResetCode, resetPassword} from '@services/auth';
import {Field, TextInput} from '@components/FormKit';
import {Button} from '@components/ui/Button';
import {formatKgPhone, phoneDigits} from '@utils/phone';

const MIN = 8;

export const ForgotPassword = () => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [next, setNext] = useState('');

  const ask = useMutation({
    mutationFn: requestResetCode,
    onSuccess: (r) => {
      if (r.devCode !== null) setCode(r.devCode);
    },
  });
  const reset = useMutation({mutationFn: resetPassword});

  const step: 'phone' | 'code' | 'done' = reset.isSuccess
    ? 'done'
    : ask.isSuccess
      ? 'code'
      : 'phone';
  const handleAsk = () => ask.mutate(phone);
  const handleReset = () => reset.mutate({phone, code, newPassword: next});

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-[372px]">
        <h1 className="text-[24px] font-bold -tracking-[.02em]">
          {step === 'done' ? 'Пароль сменён' : 'Сброс пароля'}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.45] text-muted-foreground">
          {step === 'phone' && 'Пришлём код на телефон аккаунта команды Skrezo.'}
          {step === 'code' &&
            `Если номер есть в команде, код уже в пути на +996 ${formatKgPhone(phone)}.`}
          {step === 'done' && 'Все прежние сессии закрыты. Войдите с новым паролем.'}
        </p>

        <div className="mt-7 flex flex-col gap-3.5">
          {step === 'phone' && (
            <>
              <Field label="Телефон">
                <TextInput
                  type="tel"
                  inputMode="tel"
                  value={formatKgPhone(phone)}
                  onChange={(e) => setPhone(phoneDigits(e.target.value))}
                  placeholder="700 000 000"
                  autoFocus
                  mono
                />
              </Field>
              <Button
                className="h-12 rounded-[10px] text-[15px]"
                disabled={phone.length !== 9 || ask.isPending}
                onClick={handleAsk}
              >
                {ask.isPending ? 'Отправляем…' : 'Получить код'}
              </Button>
            </>
          )}

          {step === 'code' && (
            <>
              <Field label="Код из SMS">
                <TextInput
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                  mono
                />
              </Field>
              <Field label={`Новый пароль (от ${MIN} знаков)`}>
                <TextInput
                  type="password"
                  value={next}
                  autoComplete="new-password"
                  onChange={(e) => setNext(e.target.value)}
                />
              </Field>
              {reset.isError && (
                <div className="rounded-[10px] bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
                  Код не подошёл или устарел. Запросите новый
                </div>
              )}
              <Button
                className="h-12 rounded-[10px] text-[15px]"
                disabled={code.length < 4 || next.length < MIN || reset.isPending}
                onClick={handleReset}
              >
                {reset.isPending ? 'Сохраняем…' : 'Сменить пароль'}
              </Button>
            </>
          )}

          {step === 'done' ? (
            <Link to="/login" replace>
              <Button className="h-12 w-full rounded-[10px] text-[15px]">Войти</Button>
            </Link>
          ) : (
            <Link
              to="/login"
              className="self-center text-[13px] text-muted-foreground hover:text-primary"
            >
              Вспомнил пароль — ко входу
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
